import React, { useState, useEffect, useMemo } from 'react';
import {
    DetailsList,
    DetailsListLayoutMode,
    SelectionMode,
    CommandBar,
    Stack,
    Text,
    TextField,
    DatePicker,
    Dropdown,
    Panel,
    PanelType,
    PrimaryButton,
    DefaultButton,
    Link,
    Spinner,
    MessageBar,
    MessageBarType,
} from '@fluentui/react';

import {
    useAdminApi,
    apiErrorMessage,
    PLATFORM_LABELS,
    CONTENT_CHANNEL_LABELS,
    platformLabel,
    channelsLabel,
    toDateString,
    fromDateString,
    formatDate,
    formatDay,
    formatDateTime,
    formatMoney,
    homeStyles,
    sectionStyles,
    mutedText,
} from '../common';

// ทะเบียนแคมเปญ (GET / PUT /marketing/campaigns) ใช้ผูกค่าโฆษณาและ lead กับแคมเปญ และตั้งงบ / เป้าต้นทุนต่อ lead ให้อีเมลแจ้งเตือน
// แคมเปญ content (ไม่ใช่โฆษณา) ผูก lead ด้วย utm_campaign อย่างเดียว ไม่มี campaign ID / งบ / เป้า

const STATUS_LABELS = {
    active: 'กำลังทำงาน',
    paused: 'หยุดชั่วคราว',
    ended: 'จบแล้ว',
};

const KIND_LABELS = {
    ads: 'โฆษณา',
    content: 'Content (ไม่ใช่โฆษณา)',
};

const KIND_OPTIONS = Object.entries(KIND_LABELS).map(([key, text]) => ({ key, text }));

const CHANNEL_OPTIONS = Object.entries(CONTENT_CHANNEL_LABELS).map(([key, text]) => ({ key, text }));

const PLATFORM_OPTIONS = Object.entries(PLATFORM_LABELS)
    .filter(([key]) => key !== 'organic' && key !== 'content')
    .map(([key, text]) => ({ key, text }));

const STATUS_OPTIONS = Object.entries(STATUS_LABELS).map(([key, text]) => ({ key, text }));

const EMPTY_FORM = {
    id: null,
    kind: 'ads',
    platform: 'google-ads',
    channels: [],
    name: '',
    utmCampaign: '',
    platformCampaignIds: [],
    startDate: null,
    endDate: null,
    dailyBudget: '',
    targetCpa: '',
    status: 'active',
    notes: '',
};

function formOf(campaign) {
    return {
        ...EMPTY_FORM,
        ...campaign,
        kind: campaign.kind || 'ads',
        channels: campaign.channels || [],
        utmCampaign: campaign.utmCampaign || '',
        platformCampaignIds: (campaign.platformCampaignIds || []).map(String),
        dailyBudget: campaign.dailyBudget ?? '',
        targetCpa: campaign.targetCpa ?? '',
        notes: campaign.notes || '',
    };
}

const Campaigns = () => {

    const request = useAdminApi();

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [refresh, setRefresh] = useState(0);
    const [statusFilter, setStatusFilter] = useState('active');

    const [form, setForm] = useState(null);
    const [extraIds, setExtraIds] = useState('');
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState('');
    const [saved, setSaved] = useState('');

    useEffect(() => {
        setLoading(true);
        setError('');

        request('get', '/marketing/campaigns')
            .then((response) => setData(response.data.data))
            .catch((err) => {
                console.log('Campaigns error: ', err);
                setError('โหลดทะเบียนแคมเปญไม่สำเร็จ');
                setData(null);
            })
            .finally(() => setLoading(false));
    }, [request, refresh]);

    const campaignById = useMemo(() => new Map((data ? data.campaigns : []).map((item) => [item.id, item])), [data]);

    const campaigns = useMemo(() => {
        if (!data) return [];
        return data.campaigns.filter((item) => statusFilter === 'all' || item.status === statusFilter);
    }, [data, statusFilter]);

    // campaign ในแพลตฟอร์มที่มีค่าใช้จ่าย 90 วันล่าสุดแต่ยังไม่ผูกกับทะเบียน
    const unregistered = useMemo(() => (data ? data.platformCampaigns.filter((item) => !item.campaignId) : []), [data]);

    // ตัวเลือก campaign ID ของแพลตฟอร์มที่เลือกในฟอร์ม: ที่ยังไม่ผูก + ที่ผูกกับแคมเปญนี้อยู่แล้ว
    const idOptions = useMemo(() => {
        if (!data || !form) return [];

        const options = data.platformCampaigns
            .filter((item) => item.platform === form.platform && (!item.campaignId || item.campaignId === form.id))
            .map((item) => ({
                key: String(item.platformCampaignId),
                text: `${item.platformCampaignName || item.platformCampaignId} (${item.platformCampaignId})`,
            }));

        for (const id of form.platformCampaignIds) {
            if (!options.some((option) => option.key === id)) options.push({ key: id, text: id });
        }

        return options;
    }, [data, form]);

    const openForm = (campaign) => {
        setForm(campaign ? formOf(campaign) : { ...EMPTY_FORM });
        setExtraIds('');
        setFormError('');
        setSaved('');
    };

    // เริ่มแคมเปญใหม่จาก campaign ในแพลตฟอร์มที่ยังไม่ผูก
    const registerFrom = (item) => {
        setForm({
            ...EMPTY_FORM,
            platform: item.platform,
            name: item.platformCampaignName || '',
            platformCampaignIds: [String(item.platformCampaignId)],
        });
        setExtraIds('');
        setFormError('');
        setSaved('');
    };

    const update = (field, value) => setForm({ ...form, [field]: value });

    const save = () => {
        const typedIds = extraIds.split(/[\s,]+/).map((id) => id.trim()).filter(Boolean);
        const isContent = form.kind === 'content';
        const body = {
            ...form,
            id: form.id || undefined,
            utmCampaign: form.utmCampaign.trim() || null,
            channels: isContent ? form.channels : [],
            platformCampaignIds: isContent ? [] : [...new Set([...form.platformCampaignIds, ...typedIds])],
            dailyBudget: isContent || form.dailyBudget === '' ? null : form.dailyBudget,
            targetCpa: isContent || form.targetCpa === '' ? null : form.targetCpa,
        };

        setSaving(true);
        setFormError('');

        request('put', '/marketing/campaigns', body)
            .then((response) => {
                const result = response.data.data;
                setSaved(`บันทึก "${result.campaign.name}" แล้ว` + (result.relinkedSpend > 0 ? ` ผูกค่าโฆษณาใหม่ ${result.relinkedSpend} รายการ` : ''));
                setForm(null);
                setRefresh((value) => value + 1);
            })
            .catch((err) => {
                console.log('Campaign save error: ', err);
                setFormError(apiErrorMessage(err, 'บันทึกไม่สำเร็จ'));
            })
            .finally(() => setSaving(false));
    };

    const columns = [
        {
            key: 'name', name: 'แคมเปญ', minWidth: 200, maxWidth: 300, isResizable: true,
            onRender: (item) => <Link onClick={() => openForm(item)}>{item.name}</Link>,
        },
        {
            key: 'platform', name: 'แพลตฟอร์ม / ช่องทาง', minWidth: 130, maxWidth: 200, isResizable: true,
            onRender: (item) => <Text>{item.kind === 'content' ? 'Content: ' + channelsLabel(item.channels) : platformLabel(item.platform)}</Text>,
        },
        { key: 'utmCampaign', name: 'utm_campaign', fieldName: 'utmCampaign', minWidth: 120, maxWidth: 180, isResizable: true },
        { key: 'ids', name: 'campaign ID ในแพลตฟอร์ม', minWidth: 160, maxWidth: 220, isResizable: true, onRender: (item) => <Text>{(item.platformCampaignIds || []).join(', ')}</Text> },
        { key: 'period', name: 'ช่วงเวลา', minWidth: 170, maxWidth: 200, onRender: (item) => <Text>{formatDay(item.startDate)}{item.endDate ? ' – ' + formatDay(item.endDate) : item.startDate ? ' –' : ''}</Text> },
        { key: 'dailyBudget', name: 'งบต่อวัน', minWidth: 90, maxWidth: 110, onRender: (item) => <Text>{formatMoney(item.dailyBudget)}</Text> },
        { key: 'targetCpa', name: 'เป้าต้นทุนต่อ lead', minWidth: 110, maxWidth: 130, onRender: (item) => <Text>{formatMoney(item.targetCpa)}</Text> },
        { key: 'status', name: 'สถานะ', minWidth: 90, maxWidth: 110, onRender: (item) => <Text>{STATUS_LABELS[item.status] || item.status}</Text> },
        { key: 'updatedTime', name: 'แก้ล่าสุด', minWidth: 120, maxWidth: 140, onRender: (item) => <Text>{formatDateTime(item.updatedTime)}</Text> },
    ];

    const unregisteredColumns = [
        { key: 'platform', name: 'แพลตฟอร์ม', minWidth: 120, maxWidth: 160, onRender: (item) => <Text>{platformLabel(item.platform)}</Text> },
        { key: 'name', name: 'ชื่อในแพลตฟอร์ม', fieldName: 'platformCampaignName', minWidth: 200, maxWidth: 300, isResizable: true },
        { key: 'id', name: 'campaign ID', fieldName: 'platformCampaignId', minWidth: 120, maxWidth: 160 },
        { key: 'lastDate', name: 'มีค่าใช้จ่ายล่าสุด', minWidth: 120, maxWidth: 140, onRender: (item) => <Text>{formatDay(item.lastDate)}</Text> },
        { key: 'cost', name: 'ค่าใช้จ่าย 90 วัน', minWidth: 110, maxWidth: 130, onRender: (item) => <Text>{formatMoney(item.cost)}</Text> },
        { key: 'action', name: '', minWidth: 100, maxWidth: 120, onRender: (item) => <Link onClick={() => registerFrom(item)}>ลงทะเบียน</Link> },
    ];

    const commandBarItems = [
        {
            key: 'add',
            text: 'เพิ่มแคมเปญ',
            iconProps: { iconName: 'Add' },
            disabled: loading || !data,
            onClick: () => openForm(null),
        },
        {
            key: 'refresh',
            text: 'รีเฟรช',
            iconProps: { iconName: 'Refresh' },
            disabled: loading,
            onClick: () => setRefresh(refresh + 1),
        },
    ];

    const linkedCampaign = form && form.id ? campaignById.get(form.id) : null;

    return (
        <div style={homeStyles}>
            <Stack tokens={{ childrenGap: 20 }} styles={{ root: { padding: '2%' } }}>
                <Stack>
                    <Text variant="xxLarge" styles={{ root: { fontWeight: 600 } }}>ทะเบียนแคมเปญ</Text>
                    <Text styles={mutedText}>
                        ผูกค่าโฆษณาด้วย campaign ID ในแพลตฟอร์ม และผูก lead ด้วย utm_campaign หรือ gclid งบต่อวันและเป้าต้นทุนต่อ lead ใช้แจ้งเตือนในอีเมลสรุปทุกเช้า
                        แคมเปญ content (blog, โพสต์, คลิปที่ไม่ได้ลงโฆษณา) ผูก lead ด้วย utm_campaign อย่างเดียว
                    </Text>
                </Stack>

                <CommandBar items={commandBarItems} styles={{ root: { padding: 0, background: 'transparent' } }} />

                {error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
                {saved && <MessageBar messageBarType={MessageBarType.success} onDismiss={() => setSaved('')}>{saved}</MessageBar>}
                {loading && <Spinner label="กำลังโหลด..." />}

                {data && !loading && (
                    <>
                        {unregistered.length > 0 && (
                            <Stack styles={sectionStyles} tokens={{ childrenGap: 10 }}>
                                <Text variant="large" styles={{ root: { fontWeight: 600 } }}>มีค่าใช้จ่ายแต่ยังไม่ลงทะเบียน ({unregistered.length})</Text>
                                <DetailsList items={unregistered} columns={unregisteredColumns} getKey={(item) => item.platform + ':' + item.platformCampaignId}
                                    selectionMode={SelectionMode.none} layoutMode={DetailsListLayoutMode.justified} />
                            </Stack>
                        )}

                        <Stack styles={sectionStyles} tokens={{ childrenGap: 10 }}>
                            <Stack horizontal horizontalAlign="space-between" verticalAlign="end" wrap tokens={{ childrenGap: 10 }}>
                                <Text variant="large" styles={{ root: { fontWeight: 600 } }}>แคมเปญ ({campaigns.length})</Text>
                                <Dropdown label="สถานะ" selectedKey={statusFilter} styles={{ root: { width: 200 } }}
                                    onChange={(event, option) => setStatusFilter(option.key)}
                                    options={[...STATUS_OPTIONS, { key: 'all', text: 'ทั้งหมด' }]} />
                            </Stack>
                            {campaigns.length > 0
                                ? <DetailsList items={campaigns} columns={columns} getKey={(item) => item.id}
                                    selectionMode={SelectionMode.none} layoutMode={DetailsListLayoutMode.fixedColumns} />
                                : <Text styles={mutedText}>ยังไม่มีแคมเปญในสถานะนี้</Text>}
                        </Stack>
                    </>
                )}
            </Stack>

            <Panel
                isOpen={Boolean(form)}
                onDismiss={() => !saving && setForm(null)}
                type={PanelType.medium}
                headerText={form && form.id ? 'แก้แคมเปญ' : 'เพิ่มแคมเปญ'}
                closeButtonAriaLabel="ปิด"
                isFooterAtBottom={true}
                onRenderFooterContent={() => (
                    <Stack horizontal tokens={{ childrenGap: 10 }}>
                        <PrimaryButton text={saving ? 'กำลังบันทึก...' : 'บันทึก'} onClick={save}
                            disabled={saving || !form || !form.name.trim()
                                || (form.kind === 'content' && (form.channels.length === 0 || !form.utmCampaign.trim()))} />
                        <DefaultButton text="ยกเลิก" disabled={saving} onClick={() => setForm(null)} />
                    </Stack>
                )}
            >
                {form && (
                    <Stack tokens={{ childrenGap: 12 }}>
                        {formError && <MessageBar messageBarType={MessageBarType.error}>{formError}</MessageBar>}
                        <Dropdown label="ประเภท" required selectedKey={form.kind} options={KIND_OPTIONS}
                            disabled={Boolean(linkedCampaign)}
                            onChange={(event, option) => setForm({ ...form, kind: option.key, platform: option.key === 'content' ? 'content' : 'google-ads', platformCampaignIds: [], channels: [] })} />
                        {form.kind === 'content'
                            ? <Dropdown label="ช่องทาง" required multiSelect selectedKeys={form.channels} options={CHANNEL_OPTIONS}
                                placeholder="เลือกช่องทางที่ปล่อย content"
                                onChange={(event, option) => update('channels', option.selected
                                    ? [...form.channels, option.key]
                                    : form.channels.filter((channel) => channel !== option.key))} />
                            : <Dropdown label="แพลตฟอร์ม" required selectedKey={form.platform} options={PLATFORM_OPTIONS}
                                disabled={Boolean(linkedCampaign)}
                                onChange={(event, option) => setForm({ ...form, platform: option.key, platformCampaignIds: [] })} />}
                        <TextField label="ชื่อแคมเปญ" required value={form.name} maxLength={200}
                            onChange={(event, value) => update('name', value)} />
                        <TextField label="utm_campaign" value={form.utmCampaign} maxLength={100} required={form.kind === 'content'}
                            description={form.kind === 'content'
                                ? 'ใส่ค่าเดียวกันในลิงก์ทุกชิ้นของแคมเปญนี้ ตัวเล็ก a-z 0-9 _ - ตาม docs/utm-guide.md ของ marketing'
                                : 'ตัวเล็ก a-z 0-9 _ - ตาม docs/utm-guide.md ของ marketing (ไม่บังคับสำหรับ Google Ads ที่ผูกด้วย gclid)'}
                            onChange={(event, value) => update('utmCampaign', value)} />
                        {form.kind !== 'content' && <>
                        <Dropdown label="campaign ID ในแพลตฟอร์ม" multiSelect selectedKeys={form.platformCampaignIds} options={idOptions}
                            placeholder={idOptions.length > 0 ? 'เลือกจาก campaign ที่มีค่าใช้จ่าย' : 'ยังไม่มี campaign ที่มีค่าใช้จ่าย'}
                            onChange={(event, option) => update('platformCampaignIds', option.selected
                                ? [...form.platformCampaignIds, option.key]
                                : form.platformCampaignIds.filter((id) => id !== option.key))} />
                        <TextField label="เพิ่ม campaign ID เอง" value={extraIds} placeholder="คั่นด้วย comma"
                            description="ใช้เมื่อ campaign ยังไม่มีค่าใช้จ่าย หรือแพลตฟอร์มที่กรอกค่าใช้จ่ายเอง"
                            onChange={(event, value) => setExtraIds(value)} />
                        </>}
                        <Stack horizontal tokens={{ childrenGap: 10 }}>
                            <DatePicker label="เริ่ม" value={fromDateString(form.startDate)} formatDate={formatDate} styles={{ root: { flex: 1 } }}
                                onSelectDate={(date) => update('startDate', date ? toDateString(date) : null)} />
                            <DatePicker label="สิ้นสุด" value={fromDateString(form.endDate)} formatDate={formatDate} styles={{ root: { flex: 1 } }}
                                minDate={fromDateString(form.startDate) || undefined}
                                onSelectDate={(date) => update('endDate', date ? toDateString(date) : null)} />
                        </Stack>
                        {(form.startDate || form.endDate) && (
                            <Link onClick={() => setForm({ ...form, startDate: null, endDate: null })}>ล้างวันที่</Link>
                        )}
                        {form.kind !== 'content' && <Stack horizontal tokens={{ childrenGap: 10 }}>
                            <TextField label="งบต่อวัน (บาท)" type="number" min={0} value={String(form.dailyBudget)} styles={{ root: { flex: 1 } }}
                                onChange={(event, value) => update('dailyBudget', value)} />
                            <TextField label="เป้าต้นทุนต่อ lead (บาท)" type="number" min={0} value={String(form.targetCpa)} styles={{ root: { flex: 1 } }}
                                description="lead = สมัคร หรือขอใบเสนอราคา"
                                onChange={(event, value) => update('targetCpa', value)} />
                        </Stack>}
                        <Dropdown label="สถานะ" selectedKey={form.status} options={STATUS_OPTIONS}
                            onChange={(event, option) => update('status', option.key)} />
                        <TextField label="หมายเหตุ" multiline rows={3} value={form.notes} maxLength={1000}
                            onChange={(event, value) => update('notes', value)} />
                    </Stack>
                )}
            </Panel>
        </div>
    );
};

export default Campaigns;
